import { LinearProgress, Stack, Typography } from '@mui/material';

export interface PasswordStrengthIndicatorProps {
  password: string;
}

type Strength = { label: string; value: number; color: 'error' | 'warning' | 'success' };

function scoreOf(password: string): Strength {
  if (!password) return { label: '', value: 0, color: 'error' };

  const rulesMet = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /\d/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ].filter(Boolean).length;

  if (rulesMet <= 2) return { label: 'Weak', value: 33, color: 'error' };
  if (rulesMet <= 4) return { label: 'Fair', value: 66, color: 'warning' };
  return { label: 'Strong', value: 100, color: 'success' };
}

/** A live meter next to `PasswordRequirements` on Register/Reset/Change-Password forms — uses the
 * same rule set as `passwordSchema` so it never disagrees with the backend's own validation. */
export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const strength = scoreOf(password);
  if (!password) return null;

  return (
    <Stack spacing={0.5}>
      <LinearProgress
        variant="determinate"
        value={strength.value}
        color={strength.color}
        sx={{ borderRadius: 1, height: 6 }}
      />
      <Typography variant="caption" color="text.secondary">
        Password strength: {strength.label}
      </Typography>
    </Stack>
  );
}
